export type CompassGeocodeParams = {
	/**
	 * The street address you want to geocode.
	 */
	address?: string;
	/**
	 * The city or town.
	 */
	city?: string;
	/**
	 * The country name or two letter ISO 3166-1 country code.
	 */
	country?: string;
	/**
	 * The comma-separated latitude and longitude geographic coordinates.
	 */
	latlng?: string;
	/**
	 * The postal code.
	 */
	postalCode?: string;
	/**
	 * The state.
	 */
	state?: string;
	/**
	 * Google place id.
	 */
	googlePlaceId?: string;
};

type GenericLocation = {
	latitude: number;
	longitude: number;
	country?: string;
	state?: string;
	city?: string;
	neighborhood?: string;
};

type CompassGeocodeLocationResponse = GenericLocation & {
	full_address: string;
	postal_code: string;
	street_name: string;
	street_number: string;
	google_place_id: string;
};

export type CompassGeocodeLocation = GenericLocation & {
	fullAddress: string;
	postalCode?: string;
	streetName: string;
	streetNumber: string;
	googlePlaceId: string;
	google_postcode_localities?: string[];
};

type GeocodeResponse = {
	data: CompassGeocodeLocation;
};

export class CompassAPI {
	baseURL: string;
	authToken: string;

	constructor(baseURL: string, authToken: string) {
		this.baseURL = baseURL;
		this.authToken = authToken;
	}

	async geocode(params: CompassGeocodeParams): Promise<GeocodeResponse> {
		const url = new URL('/api/v2/maps/geocode', this.baseURL);
		url.search = new URLSearchParams(
			this._parseCompassGeocodeParamsToAPI(params),
		).toString();

		const response = await fetch(url.toString(), {
			method: 'GET',
			headers: {
				'Content-Type': 'application/json',
				Authorization: `Token ${this.authToken}`,
			},
		});

		if (!response.ok) {
			throw new Error('Failed to get geocoded location from Compass');
		}

		try {
			const data = (await response.json()) as CompassGeocodeLocationResponse;

			return {
				data: this._parseGeocodeLocationFromAPI(data),
			};
		} catch (error) {
			throw new Error('Failed to parse geocoded location from Compass');
		}
	}

	_parseGeocodeLocationFromAPI(
		compassGeocodeLocation: CompassGeocodeLocationResponse,
	): CompassGeocodeLocation {
		const {
			full_address,
			postal_code,
			street_name,
			street_number,
			google_place_id,
			...rest
		} = compassGeocodeLocation;
		return {
			fullAddress: full_address,
			postalCode: postal_code,
			streetName: street_name,
			streetNumber: street_number,
			googlePlaceId: google_place_id,
			...rest,
		};
	}

	_parseCompassGeocodeParamsToAPI(compassGeocodeParams: CompassGeocodeParams) {
		const { postalCode, googlePlaceId, ...rest } = compassGeocodeParams;
		const mapped = {
			postal_code: postalCode,
			google_place_id: googlePlaceId,
			...rest,
		};

		return Object.entries(mapped).reduce((params, [key, value]) => {
			const obj: Record<string, string> = {
				...params,
			};
			if (value != null) {
				// eslint-disable-next-line security/detect-object-injection
				obj[key] = value;
			}
			return obj;
		}, {});
	}
}
